/**
 * ValidationManuelleTab — Onglet plein écran Super Admin.
 *
 * V36.56 — Renommé "Majorations personnalisées".
 * Plus de workflow de blocage manuel : depuis V36.56, le moteur d'éligibilité
 * applique automatiquement un taux de majoration personnalisée quand le
 * dossier dépasserait l'ancien seuil de validation manuelle.
 *
 * Cet onglet affiche désormais :
 *  - Justificatifs de qualifications / prévention en attente (workflow inchangé)
 *  - Historique des dossiers d'arbitrage RCDS (legacy + nouveaux dossiers
 *    avec majoration appliquée — visualisation seule)
 *
 * Endpoint : GET /api/admin/qualifications/pending?limit=20&offset=0
 *           POST /api/admin/membres/{id}/qualifications/{doc_id}/approve
 *           POST /api/admin/membres/{id}/qualifications/{doc_id}/reject
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Award, Eye, Check, XCircle, Clock, ChevronLeft, ChevronRight,
  Loader2, RefreshCw, Building2, Mail, FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { getAuthHeader } from '../services/tokenService';
import { MajorationsHistoriqueSection } from './MajorationsHistoriqueSection';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

// Catégorisation des justificatifs (visuelle + filtrable)
// - "prévention" : PV de réception, références travaux, attestations sécurité (Couche 4)
// - "qualification" : Qualibat, Qualifelec, RGE et autres qualifications professionnelles
// - "arbitrage" : dossiers RCDS BATICO sortis en VALIDATION_MANUELLE (depuis simulateur)
const PREVENTION_KEYS = new Set(['pv_reception', 'references_travaux']);
const getDocCategory = (item) => {
  if (item?._source === 'arbitrage') return 'arbitrage';
  return PREVENTION_KEYS.has(item?.qualification_key) ? 'prevention' : 'qualification';
};
const CATEGORY_META = {
  qualification: { label: 'Qualification', icon: '📜', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  prevention: { label: 'Prévention', icon: '📋', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  arbitrage: { label: 'Majoration RCDS', icon: '✕', classes: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const ValidationManuelleTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Filtre par catégorie : "all" | "qualification" | "prevention"
  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, dRes] = await Promise.all([
        axios.get(
          `${API}/api/admin/qualifications/pending?limit=${PAGE_SIZE}&offset=${offset}`,
          { headers: getAuthHeader() },
        ).catch(() => ({ data: { total: 0, items: [] } })),
        axios.get(
          `${API}/api/admin/dossiers-arbitrage/pending?limit=${PAGE_SIZE}&offset=${offset}`,
          { headers: getAuthHeader() },
        ).catch(() => ({ data: { total: 0, items: [] } })),
      ]);
      const qItems = (qRes.data?.items || []).map((i) => ({ ...i, _source: 'qualification' }));
      const dItems = (dRes.data?.items || []).map((i) => ({ ...i, _source: 'arbitrage' }));
      setItems([...qItems, ...dItems]);
      setTotal((qRes.data?.total || 0) + (dRes.data?.total || 0));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => { load(); }, [load]);

  const handleView = async (doc) => {
    try {
      const res = await fetch(
        `${API}/api/admin/membres/${doc.membre_id}/qualifications/${doc.id}/file`,
        { headers: getAuthHeader() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Impossible d\'ouvrir le fichier');
    }
  };

  const handleApprove = async (doc) => {
    setBusyId(doc.id);
    try {
      if (doc._source === 'arbitrage') {
        // Pour un dossier d'arbitrage on demande un coefficient libre + durée
        const rawCoef = window.prompt(
          'Coefficient libre à appliquer pour ce dossier ?\n(ex: 1.30 = +30%, 1.0 = pas de majoration)',
          '1.30'
        );
        if (rawCoef == null) { setBusyId(null); return; }
        const coef = parseFloat(String(rawCoef).replace(',', '.'));
        if (!coef || coef <= 0 || coef > 5) {
          toast.error('Coefficient invalide (doit être entre 0 et 5)');
          setBusyId(null);
          return;
        }
        const rawMonths = window.prompt(
          'Durée de validité en mois (1 à 60) ?',
          '12'
        );
        if (rawMonths == null) { setBusyId(null); return; }
        const months = parseInt(String(rawMonths).trim(), 10);
        if (!months || months < 1 || months > 60) {
          toast.error('Durée invalide (1 à 60 mois)');
          setBusyId(null);
          return;
        }
        await axios.post(
          `${API}/api/admin/dossiers-arbitrage/${doc.id}/approve`,
          { coefficient_libre: coef, valid_until_months: months, comment: 'Approuvé par arbitrage manuel' },
          { headers: getAuthHeader() },
        );
        toast.success(`Dossier approuvé : coef ×${coef}, valable ${months} mois`);
      } else {
        await axios.post(
          `${API}/api/admin/membres/${doc.membre_id}/qualifications/${doc.id}/approve`,
          { comment: 'Validé' },
          { headers: getAuthHeader() },
        );
        toast.success('Justificatif validé');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur approbation');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error('Motif obligatoire');
      return;
    }
    setBusyId(rejectTarget.id);
    try {
      const url = rejectTarget._source === 'arbitrage'
        ? `${API}/api/admin/dossiers-arbitrage/${rejectTarget.id}/reject`
        : `${API}/api/admin/membres/${rejectTarget.membre_id}/qualifications/${rejectTarget.id}/reject`;
      await axios.post(
        url,
        { comment: rejectReason.trim() },
        { headers: getAuthHeader() },
      );
      toast.success(rejectTarget._source === 'arbitrage' ? 'Dossier d\'arbitrage rejeté' : 'Justificatif rejeté');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur rejet');
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // Filtrage local par catégorie (qualification vs prévention vs arbitrage)
  const filteredItems = categoryFilter === 'all'
    ? items
    : items.filter((d) => getDocCategory(d) === categoryFilter);
  const countByCategory = items.reduce((acc, d) => {
    const cat = getDocCategory(d);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="validation-manuelle-tab">
      {/* V36.57 — Historique des taux personnalisés appliqués par membre */}
      <MajorationsHistoriqueSection />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Award className="h-6 w-6 text-violet-500" />
                Majorations personnalisées
              </CardTitle>
              <CardDescription className="mt-1">
                <strong>Justificatifs</strong> (qualifications, prévention) en attente
                de validation, et <strong>historique</strong> des dossiers RCDS BATICO
                avec majoration personnalisée appliquée. Depuis V36.56, les dossiers
                dépassant le dernier seuil reçoivent automatiquement le taux de
                majoration par défaut (modifiable dans le simulateur).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-600 text-white border border-amber-800 text-sm px-3 py-1 font-semibold">
                {total} en attente
              </Badge>
              <Button
                size="sm" variant="outline"
                onClick={load}
                disabled={loading}
                data-testid="vm-refresh-btn"
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                <span className="ml-1.5">Rafraîchir</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filtres par catégorie */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-slate-100">
            <span className="text-[11px] text-slate-500 font-semibold mr-1">Filtrer :</span>
            <button type="button"
              onClick={() => setCategoryFilter('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                categoryFilter === 'all'
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid="vm-filter-all"
            >
              Tous · {items.length}
            </button>
            {['qualification', 'prevention', 'arbitrage'].map((cat) => {
              const meta = CATEGORY_META[cat];
              const isActive = categoryFilter === cat;
              const activeColor = cat === 'qualification' ? 'bg-blue-600 text-white border-blue-600' :
                cat === 'prevention' ? 'bg-amber-600 text-white border-amber-600' :
                'bg-rose-600 text-white border-rose-600';
              return (
                <button key={cat} type="button"
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors gap-1 inline-flex items-center ${
                    isActive ? activeColor : `${meta.classes} hover:opacity-80`
                  }`}
                  data-testid={`vm-filter-${cat}`}
                >
                  <span>{meta.icon}</span>
                  {meta.label}s · {countByCategory[cat] || 0}
                </button>
              );
            })}
          </div>

          {loading && filteredItems.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="font-medium text-slate-700">Aucun dossier en attente</p>
              <p className="text-xs text-slate-500 mt-1">
                {categoryFilter === 'all'
                  ? 'Tous les justificatifs uploadés ont été traités.'
                  : `Aucun justificatif "${CATEGORY_META[categoryFilter].label}" en attente.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Membre / Entreprise</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Qualification</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Uploadé le</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Validité</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((d, idx) => {
                    const cat = getDocCategory(d);
                    const catMeta = CATEGORY_META[cat];
                    const isArbitrage = cat === 'arbitrage';
                    return (
                    <tr
                      key={d.id}
                      className={`border-b border-slate-100 hover:bg-amber-50/30 transition-colors ${idx % 2 ? 'bg-slate-50/40' : ''}`}
                      data-testid={`vm-row-${d.id}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-800 flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          {d.membre_entreprise || (isArbitrage ? (d.submitted_by_email || '—') : '—')}
                        </div>
                        {(d.membre_prenom || d.membre_nom) && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {`${d.membre_prenom || ''} ${d.membre_nom || ''}`.trim()}
                          </div>
                        )}
                        {d.membre_email && (
                          <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" /> {d.membre_email}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-700 flex items-center gap-1.5">
                          <span title={catMeta.label}>{catMeta.icon}</span>
                          {isArbitrage
                            ? `Dossier RCDS ${d.famille_rcds || 'BATICO'} — ${d.classe_dossier || ''}`
                            : (d.qualification_label || d.qualification_key)}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <span className={`inline-block text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${catMeta.classes} border`}>
                            {catMeta.label}
                          </span>
                          {isArbitrage ? (
                            <span className="text-[11px] text-slate-500" title={d.motifs?.join(' · ')}>
                              Score {d.score_global ?? '—'} · {(d.motifs || []).length} motif(s)
                              {Number(d.cotisation_suggeree) > 0 && (
                                <> · Cotis. {Number(d.cotisation_suggeree).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</>
                              )}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-500 flex items-center gap-1 truncate max-w-[200px]">
                              <FileText className="h-3 w-3" /> {d.original_filename || d.filename}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-xs">
                          <Clock className="h-3 w-3 text-slate-400" /> {formatDate(d.uploaded_at || d.submitted_at)}
                        </div>
                        {d.uploaded_by_name && (
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            par {d.uploaded_by_name}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">
                        {isArbitrage ? <span className="text-slate-400 italic">N/A</span> : (d.valid_until || <span className="text-slate-400 italic">—</span>)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {!isArbitrage && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => handleView(d)}
                              className="h-8 px-2 gap-1 text-slate-700"
                              title="Visualiser le justificatif"
                              data-testid={`vm-view-${d.id}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              <span className="text-xs">Voir</span>
                            </Button>
                          )}
                          {isArbitrage ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(d)}
                                disabled={busyId === d.id}
                                className="h-8 px-3 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                                title="Approuver la simulation avec coefficient libre"
                                data-testid={`vm-approve-${d.id}`}
                              >
                                {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                <span className="text-xs">Valider la simulation</span>
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setRejectTarget(d)}
                                disabled={busyId === d.id}
                                className="h-8 px-3 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold"
                                title="Rejeter la simulation"
                                data-testid={`vm-reject-${d.id}`}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                                <span className="text-xs">Rejeter</span>
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(d)}
                                disabled={busyId === d.id}
                                className="h-8 px-3 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                                title="Approuver"
                                data-testid={`vm-approve-${d.id}`}
                              >
                                {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                <span className="text-xs">Valider</span>
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setRejectTarget(d)}
                                disabled={busyId === d.id}
                                className="h-8 px-3 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold"
                                title="Rejeter"
                                data-testid={`vm-reject-${d.id}`}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                                <span className="text-xs">Rejeter</span>
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                Page {currentPage} / {totalPages} · {total} dossier{total > 1 ? 's' : ''} au total
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  data-testid="vm-prev-page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Précédent
                </Button>
                <Button
                  size="sm" variant="outline"
                  disabled={offset + PAGE_SIZE >= total || loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  data-testid="vm-next-page"
                >
                  Suivant <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog motif de rejet */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-rose-600" />
              Rejeter ce justificatif
            </DialogTitle>
            <DialogDescription>
              Motif obligatoire — il sera visible par les autres administrateurs.
            </DialogDescription>
          </DialogHeader>
          {rejectTarget && (
            <div className="text-sm bg-slate-50 rounded-md p-2 border border-slate-200">
              <div className="font-medium text-slate-800">
                {rejectTarget.membre_entreprise} — {rejectTarget.qualification_label}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {rejectTarget.original_filename}
              </div>
            </div>
          )}
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex : document illisible, expiré, ne correspond pas à la qualification..."
            rows={4}
            data-testid="vm-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Annuler</Button>
            <Button
              onClick={submitReject}
              disabled={!rejectReason.trim() || busyId === rejectTarget?.id}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="vm-confirm-reject"
            >
              {busyId === rejectTarget?.id && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ValidationManuelleTab;
